import { useEffect, useState } from 'react'
import { Card, StatCard, Badge, Button, Tabs, Select, Input } from '../../../shared/components/UI'
import { reportsApi, unitsApi } from '../../../shared/lib/api'
import { Activity, Scissors, Box, Zap, Flame, Layers, TrendingUp, PieChart as PieIcon, BarChart3, Clock } from 'lucide-react'

const FALLBACK = '-'
const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

function formatCompactNumber(value) {
  const num = Number(value || 0)
  if (!Number.isFinite(num)) return '0'
  return num >= 1000 ? num.toLocaleString() : num.toFixed(num % 1 === 0 ? 0 : 1)
}

function ProductionTrendChart({ data, emptyLabel = 'No trend data yet' }) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-sm text-gray-400 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
        <Activity size={24} className="mb-2 opacity-20" />
        {emptyLabel}
      </div>
    )
  }

  const width = 640
  const height = 256
  const padding = 20
  
  const isCostChart = data.some(d => d.hasOwnProperty('cost'))
  const maxValue = Math.max(...data.map(item => isCostChart ? (item.cost || 0) : ((item.cutting || 0) + (item.production || 0))), 1)
  const stepX = (width - padding * 2) / (data.length > 1 ? data.length - 1 : 1)

  const points = data.map((item, index) => {
    const x = padding + stepX * index
    
    if (isCostChart) {
      const y = height - padding - ((item.cost || 0) / maxValue) * (height - padding * 2)
      return { x, y, isCost: true, ...item }
    }

    const total = (item.cutting || 0) + (item.production || 0)
    const yTotal = height - padding - (total / maxValue) * (height - padding * 2)
    const yProd = height - padding - ((item.production || 0) / maxValue) * (height - padding * 2)
    const hProd = height - padding - yProd
    const hCut = (height - padding - yTotal) - hProd
    
    return { x, yTotal, yProd, hProd, hCut, ...item }
  })

  return (
    <div className="h-full group relative">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible">
        {/* Grids */}
        {points.map((p, i) => <line key={i} x1={p.x} y1={padding} x2={p.x} y2={height - padding} stroke="#f1f5f9" strokeWidth="1" />)}

        {/* Stacked Bars / Cost Line */}
        {points.map((p, i) => (
          <g key={i}>
            {p.isCost ? (
               <rect x={p.x - 4} y={p.y} width="8" height={height - padding - p.y} fill="url(#costGradient)" rx="2" className="animate-pulse" />
            ) : (
              <>
                <rect x={p.x - 8} y={p.yProd} width="16" height={p.hProd} fill="#10b981" rx="4" className="transition-all duration-500 hover:brightness-110" />
                <rect x={p.x - 8} y={p.yTotal} width="16" height={p.hCut} fill="#0ea5e9" opacity="0.6" rx="4" className="transition-all duration-500 hover:brightness-110" />
              </>
            )}
          </g>
        ))}

        {/* Trend Line Overlay */}
        <path 
          d={points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.isCost ? p.y : p.yTotal}`).join(' ')} 
          fill="none" stroke={isCostChart ? '#8b5cf6' : '#0ea5e9'} strokeWidth="2" strokeDasharray={isCostChart ? "0" : "4 2"} className="opacity-60" 
        />
        
        {isCostChart && (
          <defs>
             <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
             </linearGradient>
          </defs>
        )}
      </svg>
      
      {/* Legend */}
      <div className="flex justify-between items-center mt-4">
        <div className="flex gap-4">
           {isCostChart ? (
             <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-purple-500" /><span className="text-[10px] font-bold text-gray-500 uppercase">Cost / KG</span></div>
           ) : (
             <>
               <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-sky-500 opacity-60" /><span className="text-[10px] font-bold text-gray-500 uppercase">Cutting</span></div>
               <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-emerald-500" /><span className="text-[10px] font-bold text-gray-500 uppercase">Finished</span></div>
             </>
           )}
        </div>
        <div className="flex gap-6">
          {data.slice(-3).map(item => {
            const dateStr = item.name || ''
            return (
              <div key={item.name} className="flex flex-col items-end">
                <span className="text-[9px] font-black text-gray-400">{dateStr}</span>
                <h4 className="text-xs font-black text-gray-900 tracking-tighter">
                  {isCostChart ? `₹${item.cost?.toFixed(2)}` : `${formatCompactNumber((item.cutting || 0) + (item.production || 0))} KG`}
                </h4>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}


function MiniDonutChart({ data, onSegmentClick, emptyLabel = 'No chart data available' }) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-sm text-gray-400 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
        <PieIcon size={24} className="mb-2 opacity-20" />
        {emptyLabel}
      </div>
    )
  }

  const total = data.reduce((sum, item) => sum + Number(item?.value || 0), 0)
  if (!total) return <div className="h-full flex items-center justify-center text-sm text-gray-400">{emptyLabel}</div>

  const radius = 80
  const circumference = 2 * Math.PI * radius
  let currentOffset = 0

  return (
    <div className="h-full flex items-center justify-center relative select-none">
      <svg viewBox="0 0 220 220" className="w-full h-full drop-shadow-[0_20px_35px_rgba(0,0,0,0.15)] overflow-visible">
        <g transform="translate(110,110) rotate(-90)">
          {/* Background Ring - Subtle glass effect */}
          <circle r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="22" />
          
          {data.map((item, index) => {
            const fraction = Number(item.value || 0) / total
            const dashArray = circumference * fraction
            const segmentOffset = -currentOffset
            
            // Adjust dash to show small gap between segments
            const displayDash = Math.max(dashArray - 4, 0)
            
            const segment = (
              <circle
                key={`${item.name}-${index}`}
                r={radius}
                fill="none"
                stroke={item.color || COLORS[index % COLORS.length]}
                strokeWidth="20"
                strokeDasharray={`${displayDash} ${circumference - displayDash}`}
                strokeDashoffset={segmentOffset}
                strokeLinecap="round"
                onClick={() => onSegmentClick && onSegmentClick(item)}
                className="transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] cursor-pointer hover:stroke-[26px] hover:brightness-125"
              />
            )
            currentOffset += dashArray
            return segment
          })}
        </g>

        {/* Center UI - Multi-layer labels */}
        <circle cx="110" cy="110" r="58" fill="white" className="shadow-2xl" />
        <g className="pointer-events-none">
          <text x="110" y="102" textAnchor="middle" className="fill-gray-400 text-[10px] font-black uppercase tracking-[0.2em] opacity-60">Inventory</text>
          <text x="110" y="126" textAnchor="middle" className="fill-slate-900 text-[22px] font-black tracking-tighter leading-none">
            {formatCompactNumber(total)}
          </text>
          <text x="110" y="142" textAnchor="middle" className="fill-brand-500 text-[9px] font-black uppercase tracking-widest">Total KG</text>
        </g>
      </svg>
    </div>
  )
}



export default function ReportsPage() {
  const [tab, setTab] = useState('Production')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [units, setUnits] = useState([])
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    unitId: ''
  })
  const [selectedShade, setSelectedShade] = useState(null)
  
  useEffect(() => {
    unitsApi.list().then(res => setUnits(res.data.data || []))
  }, [])

  useEffect(() => {
    // We remove setData(null) to prevent blinking. 
    // Data will only be cleared on error or when explicitly reset.
    const load = async () => {
      setLoading(true)
      try {
        let res
        const params = { ...filters }
        if (tab === 'Production')         res = await reportsApi.production(params)
        if (tab === 'Inventory')          res = await reportsApi.inventory()
        if (tab === 'Wastage')            res = await reportsApi.wastage(params)
        if (tab === 'Batch Traceability') res = await reportsApi.batches(params)
        
        setData(res?.data?.data || res?.data || [])
      } catch (err) {
        console.error('Report error:', err)
        setData(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [tab, filters])

  const formatKg = (val) => `${parseFloat(val || 0).toLocaleString()} KG`

  // Helper visibility for charts
  const getTrendData = () => {
    if (tab === 'Production' && data?.trend) {
      return data.trend.map(t => ({
        name: t.date,
        cutting: parseFloat(t.cutting || 0),
        production: parseFloat(t.production || 0)
      }))
    }
    if (tab === 'Batch Traceability' && Array.isArray(data)) {
      return data.map(b => ({ name: b.batchNumber, cost: b.costPerKg })).reverse()
    }
    return []
  }

  const getPieData = (customData = null) => {
    const dataSource = customData || data
    if (tab === 'Wastage' && Array.isArray(dataSource)) {
      const groups = dataSource.reduce((acc, w) => {
        const reason = w.wastage_reason || 'Other'
        acc[reason] = { 
          value: (acc[reason]?.value || 0) + parseFloat(w.wastage_kg || 0),
          color: '#ef4444' 
        }
        return acc
      }, {})
      return Object.entries(groups).map(([name, d]) => ({ name, value: d.value, color: d.color }))
    }
    if (tab === 'Inventory' && Array.isArray(dataSource)) {
      const groups = dataSource.reduce((acc, r) => {
        const shadeName = r.shade?.name || 'Unknown'
        acc[shadeName] = {
          value: (acc[shadeName]?.value || 0) + parseFloat(r.available_weight_kg || 0),
          color: r.shade?.color || '#E5E7EB'
        }
        return acc
      }, {})
      return Object.entries(groups).map(([name, d]) => ({ name, value: d.value, color: d.color }))
    }
    return []
  }

  return (
    <div className="space-y-6">
      {/* Header & Premium Filter Control Center */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
           <h1 className="text-2xl font-black text-gray-900 tracking-tighter mb-1">Reports Center</h1>
           <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Real-time analytical insights</p>
        </div>
        <Tabs tabs={['Production', 'Inventory', 'Wastage', 'Batch Traceability']} active={tab} onChange={setTab} className="!mb-0" />
      </div>

      <div className="bg-white/70 backdrop-blur-xl border border-white shadow-2xl shadow-gray-200/50 p-6 rounded-[32px] flex flex-wrap items-end gap-5 animate-slide-up">
        <div className="flex-1 min-w-[200px] flex items-center gap-4">
           <div className="p-3 bg-brand-50 rounded-2xl text-brand-600">
              <Clock size={20} />
           </div>
           <div className="flex gap-3 flex-1">
             <div className="flex-1">
               <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">Start Date</label>
               <input 
                 type="date" 
                 className="w-full bg-gray-50/50 border-none rounded-xl px-4 py-2.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-brand-500/20 transition-all outline-none"
                 value={filters.fromDate} onChange={e => setFilters(p => ({ ...p, fromDate: e.target.value }))} 
               />
             </div>
             <div className="flex-1">
               <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">End Date</label>
               <input 
                 type="date" 
                 className="w-full bg-gray-50/50 border-none rounded-xl px-4 py-2.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-brand-500/20 transition-all outline-none"
                 value={filters.toDate} onChange={e => setFilters(p => ({ ...p, toDate: e.target.value }))} 
               />
             </div>
           </div>
        </div>

        {tab !== 'Inventory' && (
          <div className="flex-1 min-w-[180px]">
             <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">Operating Unit</label>
             <select 
               className="w-full bg-gray-50/50 border-none rounded-xl px-4 py-2.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-brand-500/20 transition-all outline-none cursor-pointer appearance-none"
               value={filters.unitId} onChange={e => setFilters(p => ({ ...p, unitId: e.target.value }))}
             >
                <option value="">All Regions</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
             </select>
          </div>
        )}

        <button 
          onClick={() => setFilters({ fromDate: '', toDate: '', unitId: '' })}
          className="bg-gray-900 text-white hover:bg-black px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-gray-200"
        >
          Reset View
        </button>
      </div>

      {loading && !data ? (
        <div className="p-20 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400 animate-pulse">Generating Report...</p>
        </div>
      ) : (
        <>
          {tab === 'Production' && data && data.batches && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Output"  value={formatKg(data.totalOutputKg)} sub="Selected Period" accent="brand" />
                <StatCard label="Batches Closed" value={data.batchesClosed || 0} sub="Work Orders" accent="green" />
                <StatCard label="Total Wastage"  value={formatKg(data.totalWastageKg)} sub="Consolidated" accent="amber" />
                <StatCard label="Avg Variance"   value={`${data.avgVariancePct || 0}%`} sub="Target vs Actual" accent="red" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
                <Card 
                  className="lg:col-span-2 premium-card overflow-hidden"
                  bodyClass="!p-0"
                >
                  <div className="p-6 border-b border-gray-50 flex items-center justify-between">
                    <div>
                       <h3 className="text-sm font-black text-gray-800 uppercase tracking-tighter flex items-center gap-2">
                         <BarChart3 size={16} className="text-brand-500" /> Production Trend (KG)
                       </h3>
                       <p className="text-[10px] text-gray-400 font-bold">Daily throughput of closed batches</p>
                    </div>
                    <Badge variant="blue" className="!bg-brand-50/50 !text-brand-600 border-none shadow-sm"><div className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse mr-1.5" />Live Trend</Badge>
                  </div>
                  <div className="p-6 h-[300px] bg-gradient-to-b from-white to-gray-50/30">
                    <ProductionTrendChart data={getTrendData()} valueKey="output" />
                  </div>
                </Card>


                <Card title="Output by Stage" className="premium-card">
                  <div className="space-y-5 mt-4">
                    {(data.stageOutput || []).map((s, idx) => {
                      const max = Math.max(...data.stageOutput.map(x => x.count || 0), 1)
                      const pct = ((s.count || 0) / max) * 100
                      const STAGE_ICONS = {
                        'Cut': Scissors,
                        'Bobbin': Box,
                        'Spindle': Zap,
                        'Boiling': Flame,
                        'Cone': Layers
                      }
                      const Icon = STAGE_ICONS[s.label] || Activity
                      
                      return (
                        <div key={s.label} className="group cursor-default">
                          <div className="flex justify-between items-end mb-2">
                            <div className="flex items-center gap-2.5">
                              <div className="p-2 rounded-lg bg-gray-50 text-gray-400 group-hover:bg-brand-50 group-hover:text-brand-600 transition-colors duration-300">
                                <Icon size={14} />
                              </div>
                              <span className="text-[13px] font-semibold text-gray-700">{s.label}</span>
                            </div>
                            <span className="text-gray-400 font-mono text-[11px] bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">{s.count} logs</span>
                          </div>
                          <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden shadow-inner relative">
                            <div 
                              className={`h-full transition-all duration-1000 ease-out stage-gradient-${(idx % 5) + 1}`} 
                              style={{ width: `${pct}%` }} 
                            >
                              <div className="absolute inset-0 shimmer opacity-20" />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </Card>
              </div>
            </div>
          )}

          {tab === 'Inventory' && Array.isArray(data) && (
            <div className="space-y-6 animate-slide-up">
              {/* Compact Interactive Palette */}
              <div className="bg-white/50 backdrop-blur-md p-2 rounded-full border border-gray-100 shadow-inner flex items-center gap-3 overflow-x-auto no-scrollbar max-w-full">
                {getPieData().map(shade => (
                  <button
                    key={shade.name}
                    onClick={() => setSelectedShade(shade)}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                      (selectedShade?.name === shade.name) 
                        ? 'bg-white shadow-md scale-105 ring-2 ring-offset-2' 
                        : 'hover:bg-white/80'
                    }`}
                    style={{ ringColor: shade.color }}
                  >
                    <div className="w-5 h-5 rounded-full shadow-inner" style={{ backgroundColor: shade.color }} />
                    <span className="text-[11px] font-black text-gray-700 whitespace-nowrap">{shade.name}</span>
                  </button>
                ))}
              </div>

              {/* Focus Shade View */}
              {selectedShade ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in zoom-in-95 duration-500">
                  {/* Hero Stats */}
                  <div className="lg:col-span-1 group relative bg-white rounded-[40px] p-8 shadow-2xl shadow-gray-200/50 border border-gray-50 overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 blur-[100px] opacity-20 pointer-events-none transition-colors duration-1000" style={{ backgroundColor: selectedShade.color }} />
                    
                    <div className="relative z-10">
                      <div className="w-20 h-20 rounded-3xl shadow-xl flex items-center justify-center p-5 mb-6 transition-transform group-hover:rotate-12 duration-500" style={{ backgroundColor: selectedShade.color }}>
                         <div className="w-full h-full rounded-lg border-4 border-white/40 shadow-inner" />
                      </div>
                      
                      <h2 className="text-3xl font-black text-gray-900 tracking-tighter mb-1">{selectedShade.name}</h2>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-8">{selectedShade.color}</p>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-3xl p-4">
                           <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Stock</p>
                           <p className="text-xl font-black text-gray-800">{selectedShade.value.toFixed(1)} <span className="text-xs text-gray-400">KG</span></p>
                        </div>
                        <div className="bg-gray-50 rounded-3xl p-4">
                           <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Share</p>
                           <p className="text-xl font-black text-brand-600">
                             {((selectedShade.value / data.reduce((s,r) => s + parseFloat(r.available_weight_kg), 0)) * 100).toFixed(0)}%
                           </p>
                        </div>
                      </div>
                      
                      <Button variant="secondary" className="w-full mt-6 !rounded-2xl" onClick={() => setSelectedShade(null)}>Close View</Button>
                    </div>
                  </div>

                  {/* Usage & Roll Details */}
                  <Card title={`${selectedShade.name} — Recent Movements`} className="lg:col-span-2 premium-card" bodyClass="!p-0 overflow-hidden">
                    <div className="overflow-x-auto max-h-[350px]">
                      <table className="data-table">
                        <thead><tr><th>Roll ID</th><th>Initial Weight</th><th>Available</th><th>Status</th><th>Last Action</th></tr></thead>
                        <tbody>
                          {Array.isArray(data) && data.filter(r => (r.shade?.name || 'Unknown') === selectedShade.name).map(r => (
                            <tr key={r.id}>
                              <td className="font-bold text-brand-600 text-xs">{r.roll_code}</td>
                              <td className="text-[11px] font-bold text-gray-400">{parseFloat(r.net_weight_kg).toFixed(2)} KG</td>
                              <td className="font-black text-gray-800">{parseFloat(r.available_weight_kg).toFixed(2)} KG</td>
                              <td><Badge variant={r.status === 'available' ? 'green' : 'amber'}>{r.status}</Badge></td>
                              <td className="text-[10px] text-gray-400">{new Date(r.updated_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
                  <Card title="Inventory Distribution" className="lg:col-span-1 premium-card overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-brand-50 blur-[80px] -mr-32 -mt-32 opacity-50" />
                    <div className="relative z-10 h-64 pt-4">
                       <MiniDonutChart data={getPieData()} onSegmentClick={setSelectedShade} />
                    </div>
                  </Card>

                  
                  <div className="lg:col-span-2 grid grid-cols-2 gap-6">
                     <div className="bg-white rounded-[40px] p-8 shadow-xl border border-gray-50 flex flex-col justify-center">
                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Total Shades</p>
                        <h3 className="text-5xl font-black text-gray-900 tracking-tighter">{getPieData().length}</h3>
                     </div>
                     <div className="bg-white rounded-[40px] p-8 shadow-xl border border-gray-50 flex flex-col justify-center">
                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Total Net Stock</p>
                        <h3 className="text-4xl font-black text-brand-600 tracking-tighter">
                          {Array.isArray(data) ? data.reduce((s, r) => s + parseFloat(r.available_weight_kg), 0).toFixed(1) : '0.0'} <span className="text-lg">KG</span>
                        </h3>
                     </div>
                     <div className="col-span-2 bg-brand-50 rounded-[40px] p-8 border border-brand-100 flex items-center justify-between">
                        <div>
                           <p className="text-xs font-black text-brand-600 uppercase tracking-widest mb-1">Most Available Shade</p>
                           <h4 className="text-xl font-black text-gray-800">
                             {getPieData().length > 0 ? getPieData().sort((a,b) => b.value - a.value)[0].name : 'N/A'}
                           </h4>
                        </div>
                        {getPieData().length > 0 && (
                          <div className="w-12 h-12 rounded-2xl shadow-lg border-2 border-white" style={{ backgroundColor: getPieData().sort((a,b) => b.value - a.value)[0].color }} />
                        )}
                     </div>
                  </div>
                </div>
              )}

              {/* Global Detailed List (Always visible but condensed) */}
              <Card title="Global Inventory Logs" className="premium-card opacity-60 hover:opacity-100 transition-opacity" bodyClass="!p-0 overflow-hidden">
                <div className="overflow-x-auto max-h-[300px]">
                  <table className="data-table">
                    <thead><tr><th>Roll #</th><th>Shade</th><th>Weight</th><th>Unit</th></tr></thead>
                    <tbody>
                      {Array.isArray(data) && data.slice(0, 10).map((r) => (
                        <tr key={r.id}>
                          <td className="td-mono font-bold text-gray-500">{r.roll_code}</td>
                          <td className="text-xs">
                            <div className="flex items-center gap-2">
                              {r.shade?.color && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.shade.color }} />}
                              {r.shade?.name || 'Unknown'}
                            </div>
                          </td>
                          <td className="font-bold">{parseFloat(r.available_weight_kg || 0).toFixed(2)}</td>
                          <td className="text-[10px] text-gray-400">{(typeof r.unit === 'object' ? r.unit?.name : r.unit) || 'Main'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}


          {tab === 'Wastage' && data && Array.isArray(data) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
              <Card title="Wastage Distribution" className="lg:col-span-1 premium-card shadow-red-100/50">
                <div className="h-64 mt-4">
                  <MiniDonutChart data={getPieData()} />
                </div>
                <div className="mt-6 space-y-2.5">
                  {getPieData().slice(0, 5).map((d) => (
                    <div key={d.name} className="flex items-center justify-between text-[11px] group">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: d.color }} />
                        <span className="text-gray-600 font-bold truncate max-w-[120px]">{d.name}</span>
                      </div>
                      <span className="font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-md">{d.value.toFixed(1)} KG</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card title="Wastage Log History" className="lg:col-span-2 premium-card" bodyClass="!p-0 overflow-hidden">
                <div className="overflow-x-auto max-h-[450px]">
                  <table className="data-table">
                    <thead><tr><th>Module</th><th>Reference</th><th>Unit</th><th>Shade</th><th>Wastage (KG)</th><th>Reason</th></tr></thead>
                    <tbody>
                      {data.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <Badge variant={r.type === 'Cutting' ? 'amber' : r.type === 'Spindle' ? 'teal' : 'blue'}>
                              {r.type}
                            </Badge>
                          </td>
                          <td className="td-mono font-bold text-gray-600">{r.batch_number}</td>
                          <td className="text-[11px] text-gray-400 font-bold">{(typeof r.unit === 'object' ? r.unit?.name : r.unit) || FALLBACK}</td>
                          <td className="text-xs font-black text-gray-700">{(typeof r.shade === 'object' ? r.shade?.name : r.shade) || FALLBACK}</td>
                          <td className="font-black text-red-600">{parseFloat(r.wastage_kg).toFixed(2)} KG</td>
                          <td className="text-[11px] text-gray-500 italic leading-relaxed py-3">
                            {r.wastage_reason || 'No reason provided'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}



          {tab === 'Batch Traceability' && Array.isArray(data) && (
            <div className="space-y-6 animate-slide-up">
              <Card title="Cost Trend (₹ Per KG)" className="premium-card">
                <div className="h-64 mt-4">
                  <ProductionTrendChart data={getTrendData()} valueKey="cost" color="#8b5cf6" />
                </div>
              </Card>


              <Card title="Recent Batches — Full Costing" className="premium-card" bodyClass="!p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Batch #</th><th>Unit</th><th>Shade</th><th>Input KG</th><th>Output KG</th><th>Cost/KG</th><th>Total Cost</th></tr></thead>
                    <tbody>
                      {Array.isArray(data) && data.length > 0 ? data.map((r) => (
                        <tr key={r.batchNumber}>
                          <td className="td-mono font-bold text-brand-600">{r.batchNumber}</td>
                          <td className="text-gray-500 text-xs">{(typeof r.unit === 'object' ? r.unit?.name : r.unit) || FALLBACK}</td>
                          <td className="text-xs font-medium">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: r.shadeColor }} />
                              {(typeof r.shade === 'object' ? r.shade?.name : r.shade) || FALLBACK}
                            </div>
                          </td>
                          <td className="text-xs font-semibold">{r.inputKg} KG</td>
                          <td className="text-xs font-semibold">{r.outputKg} KG</td>
                          <td><Badge variant={parseFloat(r.costPerKg) > 200 ? 'amber' : 'green'}>₹{r.costPerKg}</Badge></td>
                          <td className="text-gray-800 font-mono text-xs font-bold font-mono">₹{parseFloat(r.totalCost).toLocaleString()}</td>
                        </tr>
                      )) : <tr><td colSpan="7" className="py-20 text-center text-gray-400">No batch history found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  )
}
